// Libs
import { useEffect, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "react-query";
import { DateTime } from "luxon";
import { User } from "firebase/auth";
import { DocumentData, Timestamp } from "firebase/firestore";
import DatePicker from "react-datepicker";

// Local
import MembershipIssuesPage from "./MembershipIssuesPage";
import { DbRead, DbWrite } from "../../database";
import {
  ExistingStiltInvoice,
  UpdateStiltInvoice,
  StiltInvoiceManager,
  StiltInvoiceStatus,
  StiltLineItem,
  TemplatePaymentTerm,
} from "../../models/invoice";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import ViewInvoiceDialog, {
  ViewInvoiceDialogProps,
} from "../../components/Payments/ViewInvoiceDialog";
import { useUserPermissionsStore } from "../../store/user-permissions";
import { NewManualPayment } from "../../components/Invoices/RecordManualPaymentDialog";
import HandlePaymentDialog from "../../components/Invoices/HandlePaymentDialog";
import { SchedulingButton } from "../Scheduling/SchedulingContainer";
import EditInvoiceDialog from "../../components/Invoices/EditInvoiceDialog";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import * as strings from "../../strings";
import { generatePaymentUniqueLink } from "../../assets/js/generatePaymentUniqueLink";
import { logger } from "../../logging";
import {
  APIPaymentSavedCard,
  paymentMethods,
  StiltPayment_CreateAPI,
  StiltPaymentManager,
} from "../../models/stilt-payment";
import { useAuthStore } from "../../store/firebase-auth";
import { diffObjects } from "../../assets/js/object-diff";
import BaseInputSelect from "../../components/BaseInputSelect";
import { ExistingCustomer } from "../../models/customer";
import { getEmailList } from "../../utils/getEmailList";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { ExistingMembership, MembershipStatus } from "../../models/membership";
import { SiteKeyUserPermissions } from "../../models/site-key-user-permissions";

/** Membership issues */
export interface SuspendedMembershipData {
  customerID: string;
  customerName: string;
  membershipID: string;
  message?: string;
  timestampSuspended?: Timestamp;
  suspendedBy?: string;
  pendingInvoiceID?: string;
  hasSuspensionData: boolean;
  isPendingRenewal?: boolean;
}

/** Unlinked invoice data */
export interface UnlinkedInvoiceData {
  id: string;
  refPath: string;
  lineItems: StiltLineItem[];
  /** when linking an invoice to a membership, find this pbiID in the lineItems
   * array - that lineItem needs to have the membershipID added to it. */
  pbiIdToFindInLineItems: string | null;
  invoiceNumber: string;
  invoiceStatus: string;
  customerID: string;
  customerName: string;
  amount: number;
  issueDate: Timestamp | null;
  membershipItemName: string;
  suggestedMembership: ExistingMembership | null;
}

/** Unlinked task data */
export interface UnlinkedTaskData {
  id: string;
  title: string;
  customerID: string;
  timestampScheduled: Timestamp | null;
  taskType: number;
  taskStatus: number;
  suggestedMembership: ExistingMembership | null;
}

export default function MembershipIssuesContainer({
  siteKey,
}: {
  siteKey: string;
}): JSX.Element {
  const queryClient = useQueryClient();

  const [siteKeyDoc, siteKeyDocIsLoading] = useSiteKeyDocStore((state) => [
    state.siteKeyDoc,
    state.loading,
  ]);
  const userPermissions = useUserPermissionsStore(
    (state) => state.siteKeyUserPermissions,
  );
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;

  const [userDisplayNamesMap, userDisplayNamesMapIsLoading] =
    useUserDisplayNamesStore((state) => [
      state.userDisplayNames,
      state.loading,
    ]);

  function goToPaymentPage(paymentLink: string) {
    window.open(paymentLink, "_blank");
  }

  // function goToCustomerPage(customerID: string) {
  //   navigate(`${CUSTOMERS_URL}/${customerID}`);
  // }

  // function goToMembershipPage(membershipID: string) {
  //   navigate(`${MEMBERSHIPS}/${membershipID}`);
  // }

  const [suspendedMemberships, setSuspendedMemberships] = useState<
    ExistingMembership[]
  >([]);
  const [suspendedMembershipData, setSuspendedMembershipData] = useState<
    SuspendedMembershipData[]
  >([]);

  // New state for unlinked invoices and tasks
  const [unlinkedInvoices, setUnlinkedInvoices] = useState<
    UnlinkedInvoiceData[]
  >([]);
  const [unlinkedTasks, setUnlinkedTasks] = useState<UnlinkedTaskData[]>([]);

  const [isLinkingTask, setIsLinkingTask] = useState<string | null>(null);

  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);
  const [invoiceDialogProps, setInvoiceDialogProps] =
    useState<ViewInvoiceDialogProps | null>(null);
  const [currentInvoiceID, setCurrentInvoiceID] = useState<string | null>(null);
  const [isHandlePaymentDialogOpen, setIsHandlePaymentDialogOpen] =
    useState(false);

  const [selectedDueDate, setSelectedDueDate] = useState<DateTime | null>(null);
  const [selectedIssueDate, setSelectedIssueDate] = useState<DateTime>(
    DateTime.now(),
  );
  const [paymentTerms, setPaymentTerms] = useState<
    ExistingStiltInvoice["paymentTerms"] | null
  >(null);
  const [currentCustomer, setCurrentCustomer] =
    useState<ExistingCustomer | null>(null);

  const templatesPaymentTerms: Record<string, TemplatePaymentTerm> =
    siteKeyDoc?.customizations.paymentTerms ?? {};

  useEffect(() => {
    function getSuspendedMemberships() {
      const unsubscribe = DbRead.memberships.subscribeAll({
        siteKey: siteKey,
        status: MembershipStatus.Suspended,
        onChange: setSuspendedMemberships,
        onError: logger.error,
      });
      return unsubscribe;
    }

    const unsubscribeFn = getSuspendedMemberships();
    return () => unsubscribeFn && unsubscribeFn();
  }, [siteKey]);

  // Fetch unlinked invoices
  useEffect(() => {
    // Reset any existing unlinked invoices when site key changes
    setUnlinkedInvoices([]);
    fetchUnlinkedInvoices(siteKey, setUnlinkedInvoices);
  }, [siteKey]); // Depend on siteKey

  // Fetch unlinked tasks
  useEffect(() => {
    if (!userPermissions) return;
    // Reset any existing unlinked tasks when site key changes
    setUnlinkedTasks([]);
    fetchUnlinkedTasks(siteKey, userPermissions, setUnlinkedTasks);
  }, [siteKey, userPermissions]); // Depend on siteKey

  // Get invoices referenced by membership.suspensionData.pendingInvoiceID
  const suspendedMembershipInvoicesQueryResult = useQueries(
    suspendedMemberships.map((membership) => {
      return {
        queryKey: [
          "membership_issues_suspended_membership_invoices",
          siteKey,
          membership.suspensionData?.pendingInvoiceID,
        ],
        queryFn: async () => {
          if (!membership.suspensionData?.pendingInvoiceID) return null;
          return DbRead.invoices.getSingle(
            siteKey,
            membership.suspensionData.pendingInvoiceID,
          );
        },
      };
    }),
  );
  // get unlinked invoices
  const unlinkedInvoicesQueryResult = useQueries(
    unlinkedInvoices.map((invoice) => {
      return {
        queryKey: ["membership_issues_unlinked_invoices", siteKey, invoice.id],
        queryFn: async () => DbRead.invoices.getSingle(siteKey, invoice.id),
      };
    }),
  );
  // remove null results
  const invoices = suspendedMembershipInvoicesQueryResult
    .concat(unlinkedInvoicesQueryResult)
    .flatMap((invoice) => {
      if (invoice.data != null) {
        return invoice.data;
      } else {
        return [];
      }
    });

  const selectedInvoice = invoices.find((i) => i.id === currentInvoiceID);

  // Retrieve customer from selected invoice. For paying with a saved card.
  useEffect(() => {
    async function getCustomer() {
      if (!selectedInvoice) return;

      const customer = await DbRead.customers.get(
        siteKey,
        selectedInvoice.customerID,
      );
      setCurrentCustomer(customer);
    }
    getCustomer();
  }, [selectedInvoice, siteKey]);

  useEffect(() => {
    const data: SuspendedMembershipData[] = [];
    for (const m of suspendedMemberships) {
      data.push({
        customerID: m.customerID,
        customerName: m.customerName,
        membershipID: m.id,
        message: m.suspensionData?.message,
        timestampSuspended: m.suspensionData?.timestampSuspended,
        suspendedBy: m.suspensionData?.suspendedBy,
        pendingInvoiceID: m.suspensionData?.pendingInvoiceID,
        hasSuspensionData: m.suspensionData != null,
        isPendingRenewal: m.isPendingRenewal,
      });
    }
    setSuspendedMembershipData(data);
  }, [suspendedMemberships]);

  const mutateApplyManualPayment = useMutation(
    async (payment: StiltPayment_CreateAPI) => {
      await DbWrite.payments.manualPayment(payment);
    },
  );

  const mutateUpdateInvoice = useMutation(
    async (invoice: UpdateStiltInvoice) => {
      await DbWrite.invoices.update(invoice);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          "membership_issues_suspended_membership_invoices",
        );
        queryClient.invalidateQueries("membership_issues_unlinked_invoices");
      },
    },
  );

  // New functions for handling unlinked data
  async function linkInvoiceToMembership(
    invoiceID: string,
    membershipID: string,
  ): Promise<void> {
    logger.info(`Linking invoice ${invoiceID} to membership ${membershipID}`);

    const invoice = unlinkedInvoices.find(
      (invoice) => invoice.id === invoiceID,
    );
    if (!invoice) {
      logger.error(`Invoice ${invoiceID} not found in unlinkedInvoices`);
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
      return;
    }
    if (!invoice.pbiIdToFindInLineItems) {
      logger.error(`No pbiID given for invoice ${invoiceID}`);
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
      return;
    }

    const foundIndex = invoice.lineItems.findIndex(
      (lineItem) => lineItem.priceBookItemID === invoice.pbiIdToFindInLineItems,
    );
    if (foundIndex === -1) {
      logger.error(
        `lineItem with priceBookItemID ${invoice.pbiIdToFindInLineItems} not found for invoice ${invoiceID}`,
      );
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
      return;
    }

    // add the membershipID to the lineItem
    invoice.lineItems[foundIndex].membershipID = membershipID;

    try {
      await DbWrite.invoices.linkToMembership({
        invoiceRefPath: invoice.refPath,
        lineItems: invoice.lineItems,
        membershipID: membershipID,
      });
      // If successful, remove the invoice from the unlinked invoices list,
      // since the unlinked invoices list is from a GET and not a SUBSCRIPTION
      setUnlinkedInvoices((prev) =>
        prev.filter((invoice) => invoice.id !== invoiceID),
      );

      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Invoice linked to membership successfully",
        type: "success",
      });
    } catch (e) {
      logger.error(`linkInvoiceToMembership: ${JSON.stringify(e, null, 2)}`);
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link invoice to membership",
        type: "error",
      });
    }
  }

  async function linkTaskToMembership(
    taskID: string,
    membershipID: string,
  ): Promise<void> {
    try {
      logger.info(`Linking task ${taskID} to membership ${membershipID}`);

      setIsLinkingTask(taskID);

      const updateTask: DocumentData = {
        membershipIDs: [membershipID],
        isMembershipTask: true,
        timestampLastModified: Timestamp.now(),
        lastModifiedBy: firebaseUser.uid,
      };
      await DbWrite.tasks.update(updateTask, siteKey, taskID);
      logger.info(`Task updated - `, taskID);

      setIsLinkingTask(null);

      // Optimistically update the UI by removing the linked task from the list
      setUnlinkedTasks((prev) => prev.filter((task) => task.id !== taskID));

      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Task linked to membership successfully",
        type: "success",
      });
    } catch (e) {
      logger.error(`linkTaskToMembership: ${JSON.stringify(e, null, 2)}`);
      setIsLinkingTask(null);
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: "Failed to link task to membership",
        type: "error",
      });
    }
  }

  function openViewInvoiceDialog(invoiceID: string): void {
    if (!siteKeyDoc) return;

    setInvoiceDialogProps({
      siteKeyID: siteKey,
      invoiceID,
      merchantName: siteKeyDoc.name,
      merchantLogoURL:
        siteKeyDoc.customizations.accounting?.merchantLogoURL ?? null,
    });
    setCurrentInvoiceID(invoiceID);
  }

  function onClickHandlePaymentButton(invoiceID: string) {
    setIsHandlePaymentDialogOpen(true);
    setCurrentInvoiceID(invoiceID);
  }

  async function handleEditInvoiceDialogOpen() {
    if (selectedInvoice) {
      setEditInvoiceDialogOpen(true);
    }
  }

  /** send itemized invoice along with payment link */
  async function emailInvoice(
    email: string[],
    includeJobPhotos: boolean,
  ): Promise<void> {
    if (!currentInvoiceID) return;

    if (!selectedInvoice) {
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: strings.ERR_EMAILING_INVOICE,
        type: "error",
      });
      return;
    }
    if (email.length === 0) {
      addToastMessage({
        id: createToastMessageID(),
        message: strings.NO_EMAIL_FOR_CUSTOMER,
        dialog: false,
        type: "error",
      });
      return;
    }

    const paymentLink = await generatePaymentUniqueLink(
      siteKey,
      currentInvoiceID,
      "email",
    );
    if (!paymentLink) {
      logger.error("emailInvoice: missing invoice link");
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: strings.ERR_GEN_INVOICE_LINK,
        type: "error",
      });
      return;
    }

    try {
      await DbWrite.invoices.sendViaEmail({
        siteKey,
        invoiceURL: paymentLink,
        customerEmailList: email,
        includeJobPhotos: includeJobPhotos,
      });
      addToastMessage({
        id: createToastMessageID(),
        message: strings.EMAILED_CUSTOMER_INVOICE,
        dialog: false,
        type: "success",
      });
      setCurrentInvoiceID(null);
      setInvoiceDialogProps(null);
    } catch (e) {
      logger.error(`emailInvoice: ${JSON.stringify(e, null, 2)}`);
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: strings.ERR_EMAILING_INVOICE,
        type: "error",
      });
    }
  }

  async function emailReceipt(emailList: string[]): Promise<void> {
    if (!selectedInvoice) return;
    if (emailList.length === 0) {
      throw Error("emailReceipt given empty email list");
    }
    try {
      await DbWrite.payments.emailReceipt({
        siteKeyID: siteKey,
        invoiceID: selectedInvoice.id,
        customerEmailList: emailList,
      });
      addToastMessage({
        id: createToastMessageID(),
        message: strings.EMAILED_CUSTOMER_RECEIPT,
        dialog: false,
        type: "success",
      });
    } catch (e) {
      logger.error(`emailReceipt: ${JSON.stringify(e, null, 2)}`);
      addToastMessage({
        id: createToastMessageID(),
        message: strings.ERR_EMAILING_RECEIPT,
        dialog: false,
        type: "error",
      });
    }
  }

  async function sendEmailFromHandlePaymentDialog(
    emails: string[],
    shouldIncludePhotos: boolean,
  ): Promise<void> {
    if (!selectedInvoice) return;
    if (selectedInvoice.amountDue > 0) {
      await emailInvoice(emails, shouldIncludePhotos);
    } else {
      await emailReceipt(emails);
    }
  }

  async function handleGoToPaymentPage(): Promise<void> {
    if (!currentInvoiceID) return;
    const paymentLink = await generatePaymentUniqueLink(
      siteKey,
      currentInvoiceID,
      "web",
    );
    if (paymentLink) {
      goToPaymentPage(paymentLink);
    } else {
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: strings.ERROR_PAYMENT_LINK,
        type: "error",
      });
    }
  }

  async function applyManualPayment(
    formValues: NewManualPayment,
    timestampPaymentMade: DateTime,
  ): Promise<void> {
    if (firebaseUser == null) {
      logger.error("firebaseUser is null");
      return;
    }

    if (!currentInvoiceID) return;

    if (!selectedInvoice) {
      addToastMessage({
        id: createToastMessageID(),
        message: strings.ERROR_RECORD_MANUAL_PAYMENT,
        dialog: invoiceDialogProps != null,
        type: "error",
      });
      return;
    }

    const data: StiltPayment_CreateAPI = {
      ...formValues,
      stringTimestampPaymentMade: timestampPaymentMade.toISO(),
      customerID: selectedInvoice.customerID,
      billToCustomerID: selectedInvoice.billToCustomerID,
      locationID: selectedInvoice.locationID,
      invoiceID: currentInvoiceID,
      customData: {},
      createdBy: firebaseUser.uid,
      siteKey: siteKey,
      deleted: false,
    };

    const validPayment = StiltPaymentManager.parseCreate(data);
    logger.info({ validPayment });

    try {
      await mutateApplyManualPayment.mutateAsync(validPayment);
      setCurrentInvoiceID(null);
      if (
        validPayment.amount === selectedInvoice.amountDue &&
        selectedInvoice.status !== "draft" &&
        siteKeyDoc?.customizations.sendAutomatedReceiptToCustomers
      ) {
        if (selectedInvoice.email !== null) {
          addToastMessage({
            id: createToastMessageID(),
            message: strings.SUCCESS_MANUAL_PAYMENT_WITH_RECEIPT,
            dialog: invoiceDialogProps != null,
            type: "success",
          });
        } else {
          addToastMessage({
            id: createToastMessageID(),
            message: strings.SUCCESS_MANUAL_PAYMENT_NO_EMAIL,
            dialog: invoiceDialogProps != null,
            type: "info",
          });
        }
      } else {
        addToastMessage({
          id: createToastMessageID(),
          message: strings.SUCCESS_MANUAL_PAYMENT_NO_RECEIPT,
          dialog: invoiceDialogProps != null,
          type: "success",
        });
      }
    } catch (e) {
      logger.error(`applyManualPayment: ${JSON.stringify(e, null, 2)}`);
      addToastMessage({
        id: createToastMessageID(),
        message: strings.ERROR_RECORD_MANUAL_PAYMENT,
        dialog: invoiceDialogProps != null,
        type: "error",
      });
      return;
    }
  }

  async function handleUpdateInvoice(
    updatedInvoice: Partial<ExistingStiltInvoice>,
  ) {
    if (!selectedInvoice) return;

    const updatedInvoiceFields: UpdateStiltInvoice = {
      ...updatedInvoice,
      refPath: selectedInvoice.refPath,
      paymentTerms: paymentTerms,
      dueDate: selectedDueDate ? selectedDueDate.toString() : null,
      issueDate: selectedIssueDate?.toString(),
    };

    const diffInvoiceValues: DocumentData = diffObjects(
      selectedInvoice,
      updatedInvoiceFields,
    ).diff;

    if (Object.keys(diffInvoiceValues).length === 0) return;

    const updatedValues: Partial<ExistingStiltInvoice> = {
      ...diffInvoiceValues,
      id: selectedInvoice.id,
      refPath: selectedInvoice.refPath,
    };

    const validInvoice = StiltInvoiceManager.parseUpdate(updatedValues);
    logger.info({ validInvoice });

    try {
      await mutateUpdateInvoice.mutateAsync(validInvoice);
      setInvoiceDialogProps(null);
      addToastMessage({
        id: createToastMessageID(),
        dialog: false,
        message: strings.successfulUpdate(
          selectedInvoice.invoiceNumber
            ? `Invoice #${selectedInvoice.invoiceNumber}`
            : "Invoice",
        ),
        type: "success",
      });
      setCurrentInvoiceID(null);
    } catch (e) {
      logger.error(`handleUpdateInvoice: ${JSON.stringify(e, null, 2)}`);
      addToastMessage({
        id: createToastMessageID(),
        dialog: true,
        message: strings.UNEXPECTED_ERROR,
        type: "error",
      });
    }
  }

  function handlePaymentTermsChange(
    event: React.ChangeEvent<HTMLSelectElement>,
  ) {
    const value = event.target.value;
    if (value === "") {
      setPaymentTerms(null);
    } else {
      setPaymentTerms(value);
      const selectedPaymentTermTemplate = Object.entries(
        templatesPaymentTerms,
      ).find(([key, _]) => key === value);
      if (!selectedPaymentTermTemplate) return;
      const newDueDate = selectedIssueDate.plus({
        days: selectedPaymentTermTemplate[1].daysUntilDue,
      });
      setSelectedDueDate(newDueDate);
    }
  }

  async function payWithCardOnFile(args: {
    invoiceID: string;
    expiry: string;
    lastFour: number;
    amount: number;
  }): Promise<void> {
    if (!siteKeyDoc) throw Error("payWithCardOnFile missing siteKeyDoc");

    const processor = siteKeyDoc.customizations.accounting?.ziftAccountID
      ? "zift"
      : "paya";
    const data: APIPaymentSavedCard = {
      siteKeyID: siteKey,
      invoiceID: args.invoiceID,
      amount: args.amount,
      cardLastFour: args.lastFour,
      cardExpiry: args.expiry,
    };
    const valid = StiltPaymentManager.parseCreateWithSavedCard(data);
    await DbWrite.payments.createWithSavedCard(valid, processor);
  }

  const dueDatePicker = selectedInvoice && (
    <div className="flex flex-col">
      {selectedDueDate ? `Due Date` : "Due Date Not Selected"}
      <div className="flex items-center gap-4">
        <DatePicker
          selected={
            selectedDueDate
              ? selectedDueDate.toJSDate()
              : DateTime.now().toJSDate()
          }
          onChange={(date: Date) => {
            const luxonDate = DateTime.fromJSDate(date);
            setSelectedDueDate(luxonDate);
            setPaymentTerms(null);
          }}
          customInput={<SchedulingButton />}
        />
      </div>
    </div>
  );

  const issueDatePicker = selectedInvoice && (
    <div className="flex flex-col">
      {selectedIssueDate ? `Issue Date` : "Issue Date Not Selected"}
      <DatePicker
        selected={
          selectedIssueDate
            ? selectedIssueDate.toJSDate()
            : DateTime.now().toJSDate()
        }
        onChange={(date: Date) => {
          const luxonDate = DateTime.fromJSDate(date);
          setSelectedIssueDate(luxonDate);
          setPaymentTerms(null);
        }}
        customInput={<SchedulingButton />}
      />
    </div>
  );

  const paymentTermsSelector = (
    <BaseInputSelect
      inputName="paymentTerms"
      text="Payment Terms"
      admin={true}
      required={true}
      value={paymentTerms === null ? "" : paymentTerms}
      onChange={handlePaymentTermsChange}
    >
      <option value="">None</option>
      {Object.entries(templatesPaymentTerms).map(([key, paymentTerm]) => {
        return (
          <option key={key} value={key}>
            {paymentTerm.title}
          </option>
        );
      })}
    </BaseInputSelect>
  );

  const editInvoiceDialog = selectedInvoice && (
    <EditInvoiceDialog
      isDialogOpen={editInvoiceDialogOpen}
      closeDialog={() => setEditInvoiceDialogOpen(false)}
      invoiceDoc={selectedInvoice}
      handleSave={handleUpdateInvoice}
      dueDate={dueDatePicker}
      issueDate={issueDatePicker}
      paymentTerms={paymentTermsSelector}
    />
  );

  const handlePaymentDialog = isHandlePaymentDialogOpen &&
    siteKeyDoc &&
    userPermissions &&
    selectedInvoice &&
    currentCustomer && (
      <HandlePaymentDialog
        isDialogOpen={isHandlePaymentDialogOpen}
        closeDialog={() => setIsHandlePaymentDialogOpen(false)}
        goToPaymentPage={handleGoToPaymentPage}
        userIsSiteAdmin={userPermissions.permissions.isSiteAdmin === true}
        invoiceID={selectedInvoice.id}
        invoiceStatus={selectedInvoice.status}
        invoiceAmount={selectedInvoice.amountDue}
        invoiceSentToCustomer={selectedInvoice.timestampSentToCustomer}
        customer={currentCustomer}
        payWithCardOnFile={payWithCardOnFile}
        defaultIncludePhotos={
          siteKeyDoc.customizations.defaultIncludeJobPhotos ?? false
        }
        emailList={getEmailList(selectedInvoice, currentCustomer)}
        sendEmail={sendEmailFromHandlePaymentDialog}
        paymentMethods={
          siteKeyDoc.customizations.manualPaymentMethods ?? [...paymentMethods]
        }
        applyManualPayment={applyManualPayment}
      />
    );

  if (!siteKeyDoc || siteKeyDocIsLoading || userDisplayNamesMapIsLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  const viewInvoiceDialog = invoiceDialogProps && userPermissions && (
    <ViewInvoiceDialog
      isOpen={invoiceDialogProps != null}
      onClose={() => setInvoiceDialogProps(null)}
      {...invoiceDialogProps}
      handleRefund={async (paymentID, refundAmount) =>
        await handleRefund(siteKey, paymentID, refundAmount)
      }
      handlePayment={onClickHandlePaymentButton}
      userIsSiteAdmin={userPermissions.permissions.isSiteAdmin === true}
      editInvoice={handleEditInvoiceDialogOpen}
      sendEmail={emailInvoice}
      deleteInvoice={handleDeleteInvoice}
      userDisplayNamesMap={userDisplayNamesMap}
      handleDeletePayment={handleDeletePayment}
    >
      {{
        EditInvoiceDialog: editInvoiceDialog,
        HandlePaymentDialog: handlePaymentDialog,
      }}
    </ViewInvoiceDialog>
  );

  return (
    <>
      <MembershipIssuesPage
        suspendedMembershipTableData={suspendedMembershipData}
        unlinkedInvoices={unlinkedInvoices}
        unlinkedTasks={unlinkedTasks}
        openInvoiceDialog={openViewInvoiceDialog}
        collectDues={async (id) => await retryCollectDues(siteKey, id)}
        retryRenew={async (id) => await retryRenew(siteKey, id)}
        userDisplayNamesMap={userDisplayNamesMap}
        linkInvoiceToMembership={linkInvoiceToMembership}
        linkTaskToMembership={linkTaskToMembership}
        isLinkingTask={isLinkingTask}
      />
      {viewInvoiceDialog}
    </>
  );
}

async function handleDeletePayment(
  siteKeyID: string,
  paymentID: string,
): Promise<void> {
  return DbWrite.payments.delete(siteKeyID, paymentID);
}

async function handleDeleteInvoice(
  siteKeyID: string,
  invoiceID: string,
): Promise<void> {
  return DbWrite.invoices.delete(siteKeyID, invoiceID);
}

async function retryCollectDues(
  siteKey: string,
  membershipID: string,
): Promise<void> {
  return DbWrite.memberships.retryCollectDues(siteKey, membershipID);
}

async function retryRenew(
  siteKey: string,
  membershipID: string,
): Promise<void> {
  return DbWrite.memberships.retryRenew(siteKey, membershipID);
}

async function handleRefund(
  siteKey: string,
  paymentID: string,
  refundAmount: number,
): Promise<void> {
  // try/catch is in the InvoiceSummary component; don't want it here
  return DbWrite.payments.issueRefund(siteKey, paymentID, refundAmount);
}

// Helper function to fetch unlinked tasks
async function fetchUnlinkedTasks(
  siteKey: string,
  userPermissions: SiteKeyUserPermissions,
  setUnlinkedTasks: React.Dispatch<React.SetStateAction<UnlinkedTaskData[]>>,
): Promise<void> {
  try {
    // Get all membership templates
    const templates = await DbRead.membershipTemplates.getAll(siteKey);

    // Get the unique set of task types from taskGeneration
    const taskTypes = new Set<number>();
    templates.forEach((template) => {
      if (template.taskGeneration) {
        template.taskGeneration.forEach((task) => {
          taskTypes.add(task.taskType);
        });
      }
    });

    // Get all tasks with this taskType
    const tasks = await DbRead.tasks.getAllByTaskType(
      siteKey,
      userPermissions,
      Array.from(taskTypes),
    );

    // Filter out tasks where isMembershipTask is not true or where membershipIDs is undefined or empty
    const tasksWithoutMembershipID = tasks.filter(
      (task) =>
        task.isMembershipTask !== true ||
        !task.membershipIDs ||
        task.membershipIDs.length === 0,
    );

    // Get unique set of customerIDs
    const customerIDs = new Set<string>();
    tasksWithoutMembershipID.forEach((task) => {
      if (task.customerID) {
        customerIDs.add(task.customerID);
      }
    });

    // Get customers for all customerIDs
    const memberships = await Promise.all(
      Array.from(customerIDs).map((customerID) =>
        DbRead.memberships
          .getByCustomerId(siteKey, customerID)
          .catch((error) => {
            return null;
          }),
      ),
    );

    // Flatten the array of arrays
    const allMemberships = memberships.flat();
    // Filter out null memberships
    const filteredMemberships = allMemberships.filter(
      (membership) => membership !== null,
    ) as ExistingMembership[];

    // Create an array of unlinked tasks
    const unlinkedTasks: UnlinkedTaskData[] = [];
    tasksWithoutMembershipID.forEach((task) => {
      if (!task.customerID) return;

      // Find memberships with the same customerID
      const membershipsForCustomer = filteredMemberships.filter(
        (membership) => membership.customerID === task.customerID,
      );

      // Remove memberships if there is a customerLocationID and it doesn't match the invoice's customerLocationID
      const membershipsForCustomerWithMatchingLocation =
        membershipsForCustomer.filter(
          (membership) =>
            !task.customerLocationID ||
            membership.customerLocationID === task.customerLocationID,
        );

      // Find the first membership where the task's scheduled date is between the membership's start and end date
      const likelyMembership = membershipsForCustomerWithMatchingLocation.find(
        (membership) => {
          if (
            !membership.membershipStartDate ||
            !membership.membershipEndDate ||
            !task.timestampScheduled
          ) {
            return false;
          }
          return (
            membership.membershipStartDate.toMillis() <=
              task.timestampScheduled.toMillis() &&
            membership.membershipEndDate.toMillis() >=
              task.timestampScheduled.toMillis()
          );
        },
      );

      unlinkedTasks.push({
        id: task.id,
        title: task.title,
        customerID: task.customerID,
        timestampScheduled: task.timestampScheduled,
        taskType: task.taskType,
        taskStatus: task.taskStatus,
        suggestedMembership: likelyMembership ?? null,
      });
    });

    // Update state with all the unlinked tasks at once, sorted by most recent first
    setUnlinkedTasks(
      unlinkedTasks.sort((a, b) => {
        if (!a.timestampScheduled || !b.timestampScheduled) return 0;
        return (
          b.timestampScheduled.toMillis() - a.timestampScheduled.toMillis()
        );
      }),
    );
  } catch (error) {
    logger.error("Error fetching unlinked tasks:", error);
    // No need to return anything since we're updating state directly
  }
}

// Helper function to fetch unlinked invoices
async function fetchUnlinkedInvoices(
  siteKey: string,
  setUnlinkedInvoices: React.Dispatch<
    React.SetStateAction<UnlinkedInvoiceData[]>
  >,
): Promise<void> {
  try {
    // Get all membership templates
    const templates = await DbRead.membershipTemplates.getAll(siteKey);

    // Extract unique priceBookItemIDs and renewalPriceBookItemIDs
    const priceBookItemIDs = new Set<string>();

    templates.forEach((template) => {
      if (template.priceBookItemID) {
        priceBookItemIDs.add(template.priceBookItemID);
      }
      if (template.renewalPriceBookItemID) {
        priceBookItemIDs.add(template.renewalPriceBookItemID);
      }
    });

    // Price Book Item Promises
    const priceBookItemPromises = Array.from(priceBookItemIDs).map(
      (priceBookItemID) =>
        DbRead.priceBookItems.get(siteKey, priceBookItemID).catch((error) => {
          logger.error(
            `Error fetching priceBookItemID ${priceBookItemID}:`,
            error,
          );
          return null;
        }),
    );

    const priceBookItems = await Promise.all(priceBookItemPromises);

    // Fetch invoices for all priceBookItemIDs concurrently
    const invoicesPromises = Array.from(priceBookItemIDs).map(
      (priceBookItemID) =>
        DbRead.invoices
          .getByPriceBookItemID({ siteKey, priceBookItemID })
          .catch((error) => {
            logger.error(
              `Error fetching invoices for priceBookItemID ${priceBookItemID}:`,
              error,
            );
            return []; // Return empty array on error for this specific priceBookItemID
          }),
    );

    const allInvoicesByPriceBookItem = await Promise.all(invoicesPromises);

    // Flatten the array of arrays and remove canceled invoices
    const allInvoices = allInvoicesByPriceBookItem
      .flat()
      .filter((invoice) => invoice.status !== "canceled");

    // Filter out invoices that don't have a priceBookItemID
    const invoicesWithPriceBookItemID = allInvoices.filter(
      (invoice) => invoice.priceBookItems && invoice.priceBookItems.length > 0,
    );

    // Filter out invoices that don't have a membershipID
    let invoicesWithoutMembershipID = invoicesWithPriceBookItemID.filter(
      (invoice) =>
        !invoice.customData?.membershipIDs ||
        (Array.isArray(invoice.customData.membershipIDs) &&
          invoice.customData.membershipIDs.length === 0),
    );

    // Remove duplicate invoices by id
    invoicesWithoutMembershipID = invoicesWithoutMembershipID.filter(
      (invoice, index, self) =>
        index === self.findIndex((t) => t.id === invoice.id),
    );

    // Get unique set of customerIDs
    const customerIDs = new Set<string>();
    invoicesWithoutMembershipID.forEach((invoice) => {
      if (invoice.customerID) {
        customerIDs.add(invoice.customerID);
      }
    });

    // Get customers for all customerIDs
    const memberships = await Promise.all(
      Array.from(customerIDs).map((customerID) =>
        DbRead.memberships
          .getByCustomerId(siteKey, customerID)
          .catch((error) => {
            return null;
          }),
      ),
    );

    // Flatten the array of arrays
    const allMemberships = memberships.flat();
    // Filter out null memberships
    const filteredMemberships = allMemberships.filter(
      (membership) => membership !== null,
    ) as ExistingMembership[];

    // Process all invoices from all price book items
    const allFormattedInvoices: UnlinkedInvoiceData[] = [];

    invoicesWithoutMembershipID.forEach((invoice) => {
      let pbiIdToFindInLineItems: string | null = null;
      // Populate the membershipItemName with the priceBookItem title
      let membershipItemName = "Membership Item";
      invoice.lineItems?.forEach((lineItem) => {
        const priceBookItem = priceBookItems.find(
          (item) => item?.id === lineItem.priceBookItemID,
        );
        if (priceBookItem) {
          membershipItemName = priceBookItem.title;
          pbiIdToFindInLineItems = priceBookItem.id;
        }
      });

      // Find memberships with the same customerIDI
      const membershipsForCustomer = filteredMemberships.filter(
        (membership) => membership.customerID === invoice.customerID,
      );

      // Remove memberships if there is a customerLocationID and it doesn't match the invoice's customerLocationID
      const membershipsForCustomerWithMatchingLocation =
        membershipsForCustomer.filter(
          (membership) =>
            !invoice.customerLocationID ||
            membership.customerLocationID === invoice.customerLocationID,
        );

      // Find the most membership with a start date that's closest to the invoice's issue date
      // By calculating the absolute difference between the invoice's issue date and the membership's start date
      let mostRecentMembership = membershipsForCustomerWithMatchingLocation[0];
      // Loop through the memberships and find the one with the closest start date
      membershipsForCustomerWithMatchingLocation.forEach((membership) => {
        if (!membership.membershipStartDate) return;
        const diff = Math.abs(
          membership.membershipStartDate.toMillis() -
            invoice.issueDate.toMillis(),
        );

        // Skip if diff is greater than 60 days
        if (diff > 60 * 24 * 60 * 60 * 1000) return;

        if (
          !mostRecentMembership ||
          (mostRecentMembership.membershipStartDate &&
            diff <
              Math.abs(
                mostRecentMembership.membershipStartDate.toMillis() -
                  invoice.issueDate.toMillis(),
              ))
        ) {
          mostRecentMembership = membership;
        }
      });

      allFormattedInvoices.push({
        id: invoice.id,
        refPath: invoice.refPath,
        lineItems: invoice.lineItems,
        pbiIdToFindInLineItems: pbiIdToFindInLineItems,
        invoiceNumber: invoice.invoiceNumber || "",
        customerID: invoice.customerID,
        customerName: invoice.name,
        amount: invoice.totalAmount,
        issueDate: invoice.issueDate,
        membershipItemName: membershipItemName,
        invoiceStatus: invoice.status as StiltInvoiceStatus,
        suggestedMembership: mostRecentMembership ?? null,
      });
    });

    // Update state with all the unlinked invoices at once, sorted by most recent first
    setUnlinkedInvoices(
      allFormattedInvoices.sort((a, b) => {
        if (!a.issueDate || !b.issueDate) return 0;
        return b.issueDate.toMillis() - a.issueDate.toMillis();
      }),
    );
  } catch (error) {
    logger.error("Error fetching membership templates:", error);
    // No need to return anything since we're updating state directly
  }
}
